import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  LinearProgress,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { enqueueSnackbar } from "notistack";

import { CircularProgress } from "../../components";
import {
  openAmazonConsent,
  SellerCentralConnect,
} from "../../components/SellerCentralConnect";
import { BG_COLORS, BORDER_COLORS } from "../../constants";
import {
  TBrandEligibilityItem,
  TBrandEligibilityStatus,
} from "../../services/apiV2.types";
import { AsinCheckPanel } from "./partials/AsinCheckPanel";
import { BrandRow } from "./partials/BrandRow";
import { useBrandUngating } from "./useBrandUngating";

/** Rows rendered before "Show more". */
export const PAGE_SIZE = 50;

/** Used until the API reports its own limit. */
const DEFAULT_MANUAL_CHECK_MAX = 25;

export const BRAND_CONNECT_BENEFITS = [
  "See which popular brands you can list today.",
  "Find brands you can get approved for, with a direct link to apply.",
  "Paste your own ASINs to check their brands in bulk.",
];

const TABS: { status: TBrandEligibilityStatus; label: string; empty: string }[] = [
  {
    status: "ungated",
    label: "Ungated",
    empty: "No ungated brands yet.",
  },
  {
    status: "approval_required",
    label: "Approval required",
    empty: "No brands need approval.",
  },
  {
    status: "not_eligible",
    label: "Not eligible",
    empty: "No brands are blocked for your account.",
  },
];

const Panel: React.FC<React.PropsWithChildren> = ({ children }) => (
  <Box
    border={`1px solid ${BORDER_COLORS.main}`}
    borderRadius="4px"
    padding={{ xs: "16px", sm: "24px" }}
    bgcolor={BG_COLORS.default}
  >
    {children}
  </Box>
);

const BrandUngating: React.FC = () => {
  const { data, loading, error, applyingIds, reload, refresh, markApplied } =
    useBrandUngating();
  const [tab, setTab] = useState<TBrandEligibilityStatus>("ungated");
  const [query, setQuery] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  const counts = useMemo<Record<TBrandEligibilityStatus, number>>(() => {
    const result = { ungated: 0, approval_required: 0, not_eligible: 0};

    for (const item of data?.items ?? []) {
      if (item.status in result) result[item.status] += 1;
    }

    return result;
  }, [data]);

   const filtered = useMemo<TBrandEligibilityItem[]>(() => {
    const q = query.trim().toLowerCase();
    return (data?.items ?? []).filter(
      (item) =>
        item.status === tab &&
        (!q ||
          (item.brandName ?? "").toLowerCase().includes(q) ||
          item.asin.toLowerCase().includes(q)),
    );
  }, [data, tab, query]);
    

  const handleApplied = async (brandId: string) => {
    const ok = await markApplied(brandId);
    enqueueSnackbar(
      ok
        ? "Got it. We'll re-check this brand tonight."
        : "Couldn't save that. Please try again.",
      { variant: ok ? "success" : "error" },
    );
  };

  const header = (
    <Box mb="16px">
      <Typography component="h1" fontSize="22px" fontWeight={600}>
        Ungated Brands
      </Typography>
      <Typography fontSize="14px" color="text.secondary">
        We check popular brands against your Amazon seller account so you know
        which ones you can list today, and which ones you can apply for.
      </Typography>
    </Box>
  );

  if (loading) {
    return (
      <Box>
        {header}
        <Box
          height="50vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size="lg" />
        </Box>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box>
        {header}
        <Panel>
          <Typography mb="12px">We couldn't load your brand results.</Typography>
          <Button variant="outlined" onClick={reload}>
            Try again
          </Button>
        </Panel>
      </Box>
    );
  }

  if (
    data.scanState === "not_connected" ||
    data.scanState === "reconnect_required"
  ) {
    return (
      <Box>
        {header}
        <Box maxWidth="700px" mx="auto" data-testid="connect-amazon">
          <Panel>
            <SellerCentralConnect
              subtitle={
                data.scanState === "reconnect_required"
                  ? "Your Amazon connection needs to be renewed. Reconnect to check which brands you can sell."
                  : "Required to check which brands you can sell. Your first check starts right after you connect."
              }
              benefits={BRAND_CONNECT_BENEFITS}
              onConnect={() => openAmazonConsent("/brand-ungating")}
            />
          </Panel>
        </Box>
      </Box>
    );
  }

  const progress =
    data.totalBrands > 0
      ? Math.min(100, Math.round((data.checkedBrands / data.totalBrands) * 100))
      : 0;

  return (
    <Box display="flex" flexDirection="column">
      {header}

      {data.scanState === "scanning" && (
        <Box mb="16px" data-testid="scan-progress">
          <Panel>
            <Typography fontWeight={600} fontSize="14px" mb="8px">
              Checking brands: {data.checkedBrands.toLocaleString()} of{" "}
              {data.totalBrands.toLocaleString()}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress}
              aria-label="Brand check progress"
            />
            <Typography fontSize="12px" color="text.secondary" mt="8px">
              The most popular brands are checked first. Results appear here as
              they come in. The full check takes about 20–30 minutes.
            </Typography>
          </Panel>
        </Box>
      )}

      <AsinCheckPanel
        maxAsins={data.manualCheckMaxAsins || DEFAULT_MANUAL_CHECK_MAX}
        onChecked={refresh}
      />

      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ xs: "stretch", md: "center" }}
        justifyContent="space-between"
        gap="8px"
        mb="8px"
      >
        <Tabs
          value={tab}
          onChange={(_e, value: TBrandEligibilityStatus) => {
            setTab(value);
            setVisible(PAGE_SIZE);
          }}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {TABS.map((t) => (
            <Tab
              key={t.status}
              value={t.status}
              label={`${t.label} (${counts[t.status].toLocaleString()})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          ))}
        </Tabs>

        <TextField
          size="small"
          placeholder="Search brands or ASINs"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setVisible(PAGE_SIZE);
          }}
          sx={{ width: { xs: "100%", md: 260 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { "aria-label": "Search brands" },
          }}
        />
      </Stack>

      {filtered.length ? (
        <>
          <Box
            display="flex"
            flexDirection="column"
            border={`1px solid ${BORDER_COLORS.main}`}
            borderRadius="4px"
            bgcolor={BG_COLORS.default}
          >
            {filtered.slice(0, visible).map((item) => (
              <BrandRow
                key={item.brandId || `manual-${item.asin}`}
                item={item}
                applying={applyingIds.includes(item.brandId)}
                onApplied={handleApplied}
              />
            ))}
          </Box>
          {filtered.length > visible && (
            <Button
              sx={{ alignSelf: "center", mt: "12px" }}
              onClick={() => setVisible((v) => v + PAGE_SIZE)}
            >
              Show more ({(filtered.length - visible).toLocaleString()} left)
            </Button>
          )}
        </>
      ) : (
        <Panel>
          <Typography color="text.secondary" textAlign="center">
            {query.trim()
              ? `No brands match "${query.trim()}".`
              : data.scanState === "scanning"
                ? "Still checking. Results will appear here shortly."
                : TABS.find((t) => t.status === tab)?.empty}
          </Typography>
        </Panel>
      )}
    </Box>
  );
};

export default BrandUngating;
